// This storybook doesn't do what it purports to.  Instead of testing the state of the actual component,
// it defines columns and how to render them here.  Due to this, it can't be trusted to represent
// the current working state of the LeadDetailsDrawer.  This was moved by me into the new leads table,
// but it will need to be recreated.


import { http, HttpResponse } from 'msw'
import moment from 'moment'
import LeadDetailsDrawer from './LeadDetailsDrawer'
import { toDollars } from 'lib/utils'

function formatLookupValue(value, lookup) {
  if (value == null || value === '') {
    return ''
  }

  return lookup[String(value)] ?? String(value)
}

const renderLookupCell = (field, lookup) => (params) =>
  formatLookupValue(params.row[field], lookup)

export default {
  title: 'Components/LeadDetailsDrawer',
  component: LeadDetailsDrawer,
  decorators: [
    (Story) => (
      <div style={{ margin: '3em', width: '1200px' }}>
        <Story />
      </div>
    ),
  ],
  parameters: {
    zeplinLink: 'https://zpl.io/5EdM3dQ',
    layout: 'fullscreen',
    chromatic: {
      modes: {
        tall: {
          viewport: {
            height: 2500,
            width: 1500,
          },
        },
      },
    },
  },
}

const lead = {
  id: 1,
  publicId: '39bae7d4',
  degreeProgramId: 274,
  externalId: 'UMA-0000000000',
  firstName: 'Paul',
  lastName: 'Bunyon',
  email: 'bunyon@woods.com',
  phone: '8454692112',
  streetAddress: '1 Elizabeth Dr',
  city: 'Chester',
  state: 'NY',
  zipCode: '10918',
  status: 'new_lead',
  statusCode: 'V-200',
  statusChangedAt: '2023-08-01T14:33:17.303Z',
  leadAmountCents: null,
  rejectionReason: null,
  rawRequestParameters:
    '{"HS":"Yes - High School Diploma","City":"Chester","Agree":"1","Email":"bunyon@woods.com","Phone":"(845) 469-2112","State":"NY","subid":"umahcmsnsyllwpocketsnnsn01","source":"sn","variant":"3","LastName":"Bunyon","ZIP_Code":"10918","resident":"Yes","FirstName":"Paul","StartClasses":"3-6 Months","jornaya_leadid_token":"DBFE9790-38AD-5CC0-C874-13BAB3A15BF4","StreetAddress":"1 Elizabeth Drive","GraduationYear":"2010"}',
  createdAt: '2023-08-01T14:33:16.634Z',
  updatedAt: '2023-08-02T16:01:41.967Z',
  subid: 'umahcmsnsyllwpocketsnnsn01',
  source: 'sn',
  remoteIp: '98.183.198.50',
  secondaryTcpa: false,
  trustedFormCertUrl: null,
  trustedFormCertClaimed: false,
  filteredReason: null,
  userAgent:
    'Mozilla/5.0 (iPhone; CPU iPhone OS 16_1_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/16.1 Mobile/15E148 Snapchat/12.28.0.27 (like Safari/8614.2.9.0.10)',
  trackingParameters: '{"_scid":"e89103d7-bde0-47b2-b23d-c385a9261484", "_ttp": "zy8Y6Vl9zPFxY9xd03sjCwwFiH2", "google_experiments":{"Tws20ofDTV-GGYYjRE44Hg":"1"}}',
  vendorId: 1,
  billable: true,
  test: false,
  landingPageId: null,
  disposition: null,
  complyedCreativeId: 1234,
  clientId: 31,
  jornayaLeadidToken: 'DBFE9790-38AD-5CC0-C874-13BAB3A15BF4',
  clientCampaignId: 37,
  costInCents: 1398,
}

const clientLookup = {
  31: 'UMA',
}

const vendorLookup = {
  1: 'Cygnus Education',
}

const degreeProgramLookup = {
  274: 'Environmental Conservation Degree',
}

const clientCampaignLookup = {
  37: 'Platinum'
}

const statusLookup = {
  new_lead: 'New lead',
}

const yesNoLookup = {
  true: 'Yes',
  false: 'No',
}

const columns = [
  {
    headerName: 'Date',
    field: 'createdAt',
    type: 'date',
    valueFormatter: (value) => moment(value).format('M/D/YY, h:mm A'),
  },
  {
    headerName: 'Email',
    field: 'email',
  },
  {
    headerName: 'Raw request parameters',
    field: 'rawRequestParameters',
  },
  {
    headerName: 'Client',
    field: 'clientId',
    type: 'number',
    valueFormatter: (value) => formatLookupValue(value, clientLookup),
  },
  {
    headerName: 'VEGA ID',
    field: 'publicId',
  },
  {
    headerName: 'Vendor',
    field: 'vendorId',
    valueFormatter: (value) => formatLookupValue(value, vendorLookup),
  },
  {
    headerName: 'Degree Program',
    field: 'degreeProgramId',
    valueFormatter: (value) => formatLookupValue(value, degreeProgramLookup),
  },
  {
    headerName: 'Campaign',
    field: 'clientCampaignId',
    valueFormatter: (value) => formatLookupValue(value, clientCampaignLookup),
  },
  {
    headerName: 'External ID',
    field: 'externalId',
  },
  {
    headerName: 'SubID',
    field: 'subid',
  },
  {
    headerName: 'Source',
    field: 'source',
  },
  {
    headerName: 'First name',
    field: 'firstName',
  },
  {
    headerName: 'Last name',
    field: 'lastName',
  },
  {
    headerName: 'Phone Number',
    field: 'phone',
  },
  {
    headerName: 'Street Address',
    field: 'streetAddress',
  },
  {
    headerName: 'City',
    field: 'city',
  },
  {
    headerName: 'State',
    field: 'state',
  },
  {
    headerName: 'ZIP Code',
    field: 'zipCode',
  },
  {
    headerName: 'Status',
    field: 'status',
    valueFormatter: (value) => formatLookupValue(value, statusLookup),
    renderCell: renderLookupCell('status', statusLookup),
  },
  {
    headerName: 'Status Code',
    field: 'statusCode',
  },
  {
    headerName: 'Disposition',
    field: 'disposition',
    valueFormatter: (value) =>
      formatLookupValue(value, {
        not_qualified: 'Not Qualified',
        not_interested: 'Not Interested',
        closed_lost: 'Closed Lost',
        bad_lead: 'Bad Lead',
      }),
  },
  {
    headerName: 'Status changed',
    field: 'statusChangedAt',
    type: 'datetime',
    valueFormatter: (value) => moment(value).format('M/D/YY, h:mm A'),
  },
  {
    headerName: 'Billable',
    field: 'billable',
    valueFormatter: (value) => formatLookupValue(value, yesNoLookup),
    renderCell: renderLookupCell('billable', yesNoLookup),
  },
  {
    headerName: 'Lead amount',
    field: 'costInCents',
    valueFormatter: (value) => toDollars(value)
  },
  {
    headerName: 'IP Address',
    field: 'remoteIp',
  },
  {
    headerName: 'User Agent',
    field: 'userAgent',
  },
  {
    headerName: 'Secondary TCPA',
    field: 'secondaryTcpa',
    valueFormatter: (value) => formatLookupValue(value, yesNoLookup),
    renderCell: renderLookupCell('secondaryTcpa', yesNoLookup),
  },
  {
    headerName: 'Rejection reason',
    field: 'rejectionReason',
  },
  {
    headerName: 'Filtered reason',
    field: 'filteredReason',
  },
  {
    headerName: 'Tracking parameters',
    field: 'trackingParameters',
  },
  {
    headerName: 'Test',
    field: 'test',
    type: 'checkbox',
    valueFormatter: (value) => formatLookupValue(value, yesNoLookup),
    renderCell: renderLookupCell('test', yesNoLookup),
  },
  {
    headerName: 'Landing Page ID',
    field: 'landingPageId',
  },
]

const Template = (args) => {
  return (
    <LeadDetailsDrawer
      columns={args.columns}
      lead={args.lead}
      onClose={() => console.log('closed')}
    />
  )
}

export const Default = Template.bind({})
Default.args = {
  lead,
  columns,
}
Default.parameters = {
  msw: {
    handlers: [
      http.get('https://test.complyed.com/creatives/1234.json', () => {
        return HttpResponse.json({
          id: 1234,
          approved_at: '2023-08-02 01:57:47 UTC',
          ad_type: 'Video Ad',
          url: '/nothing_real.mov',
        })
      }),
    ],
  },
}

export const ComplyedLoginNeeded = Template.bind({})
ComplyedLoginNeeded.args = {
  ...Default.args,
  lead: { ...lead, complyedCreativeId: 5678 },
}
ComplyedLoginNeeded.parameters = {
  msw: {
    handlers: [
      http.get('https://test.complyed.com/creatives/5678.json', () => {
        return new HttpResponse('Forbidden', { status: 401 })
      }),
    ],
  },
}

export const NoCreativeAttachment = Template.bind({})
NoCreativeAttachment.args = {
  ...Default.args,
  lead: { ...lead, complyedCreativeId: 9999 },
}
NoCreativeAttachment.parameters = {
  msw: {
    handlers: [
      http.get('https://test.complyed.com/creatives/9999.json', () => {
        return HttpResponse.json({
          id: 1234,
          approved_at: '2023-08-02 01:57:47 UTC',
          ad_type: null,
          url: null,
        })
      }),
    ],
  },
}

export const NoCID = Template.bind({})
NoCID.args = {
  ...Default.args,
  lead: { ...lead, complyedCreativeId: null },
}
NoCID.parameters = {}
